// ISO 3166-1 alpha-2 country list + product-specific extras (Kurdistan).
// Single source of truth on the frontend; mirrors backend/iso_countries.py.
// Stored value = code; rendered label = name. Sorted alphabetically by name.
//
// Entries with `custom: true` are NOT ISO codes; `flagFromCode` returns inline
// SVG/JSX for those (since Unicode regional-indicator emoji don't exist).

import React from "react";

// Inline Kurdistan flag (red top, white middle with golden sun, green bottom).
// 21-ray sun is the canonical design; we approximate with 21 evenly-spaced rays
// emanating from the disc — small enough to ship inline.
const KURDISTAN_SVG = (() => {
  const rays = [];
  for (let i = 0; i < 21; i++) {
    const a = (i / 21) * Math.PI * 2 - Math.PI / 2;
    const x1 = 50 + Math.cos(a) * 7;
    const y1 = 33 + Math.sin(a) * 7;
    const x2 = 50 + Math.cos(a) * 13;
    const y2 = 33 + Math.sin(a) * 13;
    rays.push({ x1, y1, x2, y2 });
  }
  return { rays };
})();

function KurdistanFlag({ size = 18, testId }) {
  return (
    <span
      data-testid={testId || "flag-KU"}
      role="img"
      aria-label="Kurdistan flag"
      style={{ display: "inline-block", lineHeight: 0, verticalAlign: "middle" }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 100 66"
        width={size * (100 / 66)}
        height={size}
        style={{ borderRadius: 2, boxShadow: "0 0 0 1px rgba(255,255,255,0.08)" }}
      >
        <rect x="0" y="0" width="100" height="22" fill="#E32636" />
        <rect x="0" y="22" width="100" height="22" fill="#FFFFFF" />
        <rect x="0" y="44" width="100" height="22" fill="#2C9659" />
        {/* Sun rays */}
        {KURDISTAN_SVG.rays.map((r, i) => (
          <line key={i} x1={r.x1} y1={r.y1} x2={r.x2} y2={r.y2} stroke="#F4B600" strokeWidth="1.6" />
        ))}
        {/* Sun disc */}
        <circle cx="50" cy="33" r="6" fill="#F4B600" />
      </svg>
    </span>
  );
}

// --- Country catalog -------------------------------------------------------

export const COUNTRY_LIST = [
  { code: "AF", name: "Afghanistan" }, { code: "AX", name: "Åland Islands" },
  { code: "AL", name: "Albania" }, { code: "DZ", name: "Algeria" },
  { code: "AS", name: "American Samoa" }, { code: "AD", name: "Andorra" },
  { code: "AO", name: "Angola" }, { code: "AI", name: "Anguilla" },
  { code: "AQ", name: "Antarctica" }, { code: "AG", name: "Antigua and Barbuda" },
  { code: "AR", name: "Argentina" }, { code: "AM", name: "Armenia" },
  { code: "AW", name: "Aruba" }, { code: "XAS", name: "Assyrian", custom: true },
  { code: "AU", name: "Australia" },
  { code: "AT", name: "Austria" }, { code: "AZ", name: "Azerbaijan" },
  { code: "BS", name: "Bahamas" }, { code: "BH", name: "Bahrain" },
  { code: "BD", name: "Bangladesh" }, { code: "BB", name: "Barbados" },
  { code: "BY", name: "Belarus" }, { code: "BE", name: "Belgium" },
  { code: "BZ", name: "Belize" }, { code: "BJ", name: "Benin" },
  { code: "BM", name: "Bermuda" }, { code: "BT", name: "Bhutan" },
  { code: "BO", name: "Bolivia" },
  { code: "BQ", name: "Bonaire, Sint Eustatius and Saba" },
  { code: "BA", name: "Bosnia and Herzegovina" }, { code: "BW", name: "Botswana" },
  { code: "BV", name: "Bouvet Island" }, { code: "BR", name: "Brazil" },
  { code: "IO", name: "British Indian Ocean Territory" }, { code: "VG", name: "British Virgin Islands" },
  { code: "BN", name: "Brunei" }, { code: "BG", name: "Bulgaria" },
  { code: "BF", name: "Burkina Faso" }, { code: "BI", name: "Burundi" },
  { code: "CV", name: "Cape Verde" }, { code: "KH", name: "Cambodia" },
  { code: "CM", name: "Cameroon" }, { code: "CA", name: "Canada" },
  { code: "KY", name: "Cayman Islands" }, { code: "CF", name: "Central African Republic" },
  { code: "TD", name: "Chad" }, { code: "CL", name: "Chile" },
  { code: "CN", name: "China" }, { code: "CX", name: "Christmas Island" },
  { code: "CC", name: "Cocos (Keeling) Islands" }, { code: "CO", name: "Colombia" },
  { code: "KM", name: "Comoros" }, { code: "CG", name: "Congo" },
  { code: "CD", name: "DR Congo" }, { code: "CK", name: "Cook Islands" },
  { code: "CR", name: "Costa Rica" }, { code: "CI", name: "Ivory Coast" },
  { code: "HR", name: "Croatia" }, { code: "CU", name: "Cuba" },
  { code: "CW", name: "Curaçao" }, { code: "CY", name: "Cyprus" },
  { code: "CZ", name: "Czechia" }, { code: "DK", name: "Denmark" },
  { code: "DJ", name: "Djibouti" }, { code: "DM", name: "Dominica" },
  { code: "DO", name: "Dominican Republic" }, { code: "EC", name: "Ecuador" },
  { code: "EG", name: "Egypt" }, { code: "SV", name: "El Salvador" },
  { code: "GQ", name: "Equatorial Guinea" }, { code: "ER", name: "Eritrea" },
  { code: "EE", name: "Estonia" }, { code: "SZ", name: "Eswatini" },
  { code: "ET", name: "Ethiopia" }, { code: "FK", name: "Falkland Islands" },
  { code: "FO", name: "Faroe Islands" }, { code: "FJ", name: "Fiji" },
  { code: "FI", name: "Finland" }, { code: "FR", name: "France" },
  { code: "GF", name: "French Guiana" }, { code: "PF", name: "French Polynesia" },
  { code: "TF", name: "French Southern Territories" },
  { code: "GA", name: "Gabon" }, { code: "GM", name: "Gambia" },
  { code: "GE", name: "Georgia" }, { code: "DE", name: "Germany" },
  { code: "GH", name: "Ghana" }, { code: "GI", name: "Gibraltar" },
  { code: "GR", name: "Greece" }, { code: "GL", name: "Greenland" },
  { code: "GD", name: "Grenada" }, { code: "GP", name: "Guadeloupe" },
  { code: "GU", name: "Guam" }, { code: "GT", name: "Guatemala" },
  { code: "GG", name: "Guernsey" }, { code: "GN", name: "Guinea" },
  { code: "GW", name: "Guinea-Bissau" }, { code: "GY", name: "Guyana" },
  { code: "HT", name: "Haiti" },
  { code: "HM", name: "Heard Island and McDonald Islands" },
  { code: "HN", name: "Honduras" }, { code: "HK", name: "Hong Kong" },
  { code: "HU", name: "Hungary" }, { code: "IS", name: "Iceland" },
  { code: "IN", name: "India" }, { code: "ID", name: "Indonesia" },
  { code: "IR", name: "Iran" }, { code: "IQ", name: "Iraq" },
  { code: "IE", name: "Ireland" }, { code: "IM", name: "Isle of Man" },
  { code: "IT", name: "Italy" },
  { code: "JM", name: "Jamaica" }, { code: "JP", name: "Japan" },
  { code: "JE", name: "Jersey" }, { code: "JO", name: "Jordan" },
  { code: "KZ", name: "Kazakhstan" }, { code: "KE", name: "Kenya" },
  { code: "KI", name: "Kiribati" }, { code: "XK", name: "Kosovo" },
  { code: "KP", name: "North Korea" },
  { code: "KR", name: "South Korea" }, { code: "KU", name: "Kurdistan", custom: true },
  { code: "KW", name: "Kuwait" }, { code: "KG", name: "Kyrgyzstan" },
  { code: "LA", name: "Laos" }, { code: "LV", name: "Latvia" },
  { code: "LB", name: "Lebanon" }, { code: "LS", name: "Lesotho" },
  { code: "LR", name: "Liberia" }, { code: "LY", name: "Libya" },
  { code: "LI", name: "Liechtenstein" }, { code: "LT", name: "Lithuania" },
  { code: "LU", name: "Luxembourg" }, { code: "MO", name: "Macao" },
  { code: "MG", name: "Madagascar" }, { code: "MW", name: "Malawi" },
  { code: "MY", name: "Malaysia" }, { code: "MV", name: "Maldives" },
  { code: "ML", name: "Mali" }, { code: "MT", name: "Malta" },
  { code: "MH", name: "Marshall Islands" }, { code: "MQ", name: "Martinique" },
  { code: "MR", name: "Mauritania" }, { code: "MU", name: "Mauritius" },
  { code: "YT", name: "Mayotte" }, { code: "MX", name: "Mexico" },
  { code: "FM", name: "Micronesia" }, { code: "MD", name: "Moldova" },
  { code: "MC", name: "Monaco" }, { code: "MN", name: "Mongolia" },
  { code: "ME", name: "Montenegro" }, { code: "MS", name: "Montserrat" },
  { code: "MA", name: "Morocco" }, { code: "MZ", name: "Mozambique" },
  { code: "MM", name: "Myanmar" }, { code: "NA", name: "Namibia" },
  { code: "NR", name: "Nauru" }, { code: "NP", name: "Nepal" },
  { code: "NL", name: "Netherlands" }, { code: "NC", name: "New Caledonia" },
  { code: "NZ", name: "New Zealand" }, { code: "NI", name: "Nicaragua" },
  { code: "NE", name: "Niger" }, { code: "NG", name: "Nigeria" },
  { code: "NU", name: "Niue" }, { code: "NF", name: "Norfolk Island" },
  { code: "MK", name: "North Macedonia" }, { code: "MP", name: "Northern Mariana Islands" },
  { code: "NO", name: "Norway" }, { code: "OM", name: "Oman" },
  { code: "PK", name: "Pakistan" }, { code: "PW", name: "Palau" },
  { code: "PS", name: "Palestine" }, { code: "PA", name: "Panama" },
  { code: "PG", name: "Papua New Guinea" }, { code: "PY", name: "Paraguay" },
  { code: "PE", name: "Peru" }, { code: "PH", name: "Philippines" },
  { code: "PN", name: "Pitcairn" }, { code: "PL", name: "Poland" },
  { code: "PT", name: "Portugal" }, { code: "PR", name: "Puerto Rico" },
  { code: "QA", name: "Qatar" }, { code: "RE", name: "Réunion" },
  { code: "RO", name: "Romania" }, { code: "RU", name: "Russia" },
  { code: "RW", name: "Rwanda" }, { code: "BL", name: "Saint Barthélemy" },
  { code: "SH", name: "Saint Helena" }, { code: "KN", name: "Saint Kitts and Nevis" },
  { code: "LC", name: "Saint Lucia" }, { code: "MF", name: "Saint Martin" },
  { code: "PM", name: "Saint Pierre and Miquelon" }, { code: "VC", name: "Saint Vincent and the Grenadines" },
  { code: "WS", name: "Samoa" }, { code: "SM", name: "San Marino" },
  { code: "ST", name: "Sao Tome and Principe" }, { code: "SA", name: "Saudi Arabia" },
  { code: "SN", name: "Senegal" }, { code: "RS", name: "Serbia" },
  { code: "SC", name: "Seychelles" }, { code: "SL", name: "Sierra Leone" },
  { code: "SG", name: "Singapore" }, { code: "SX", name: "Sint Maarten" },
  { code: "SK", name: "Slovakia" }, { code: "SI", name: "Slovenia" },
  { code: "SB", name: "Solomon Islands" }, { code: "SO", name: "Somalia" },
  { code: "ZA", name: "South Africa" }, { code: "GS", name: "South Georgia" },
  { code: "SS", name: "South Sudan" }, { code: "ES", name: "Spain" },
  { code: "LK", name: "Sri Lanka" }, { code: "SD", name: "Sudan" },
  { code: "SR", name: "Suriname" }, { code: "SJ", name: "Svalbard and Jan Mayen" },
  { code: "SE", name: "Sweden" }, { code: "CH", name: "Switzerland" },
  { code: "SY", name: "Syria" }, { code: "TW", name: "Taiwan" },
  { code: "TJ", name: "Tajikistan" }, { code: "TZ", name: "Tanzania" },
  { code: "TH", name: "Thailand" }, { code: "TL", name: "Timor-Leste" },
  { code: "TG", name: "Togo" }, { code: "TK", name: "Tokelau" },
  { code: "TO", name: "Tonga" }, { code: "TT", name: "Trinidad and Tobago" },
  { code: "TN", name: "Tunisia" }, { code: "TR", name: "Turkey" },
  { code: "TM", name: "Turkmenistan" }, { code: "TC", name: "Turks and Caicos Islands" },
  { code: "TV", name: "Tuvalu" }, { code: "UG", name: "Uganda" },
  { code: "UA", name: "Ukraine" }, { code: "AE", name: "United Arab Emirates" },
  { code: "GB", name: "United Kingdom" }, { code: "US", name: "United States" },
  { code: "UM", name: "United States Minor Outlying Islands" },
  { code: "VI", name: "United States Virgin Islands" },
  { code: "UY", name: "Uruguay" }, { code: "UZ", name: "Uzbekistan" },
  { code: "VU", name: "Vanuatu" }, { code: "VA", name: "Vatican City" },
  { code: "VE", name: "Venezuela" }, { code: "VN", name: "Vietnam" },
  { code: "WF", name: "Wallis and Futuna" }, { code: "EH", name: "Western Sahara" },
  { code: "YE", name: "Yemen" }, { code: "ZM", name: "Zambia" },
  { code: "ZW", name: "Zimbabwe" },
].sort((a, b) => a.name.localeCompare(b.name));

const _BY_CODE = Object.fromEntries(COUNTRY_LIST.map((c) => [c.code, c]));

/**
 * Common-name aliases → ISO alpha-2 code. Used by any future searchable
 * combobox so that typing "USA" / "UK" / "Holland" / "Swaziland" / "Burma"
 * resolves to the canonical entry. Kept lower-cased for case-insensitive
 * lookup.
 */
export const COUNTRY_ALIASES = {
  // United States
  "usa": "US", "u.s.a.": "US", "u.s.": "US", "united states of america": "US", "america": "US",
  // United Kingdom & home nations
  "uk": "GB", "u.k.": "GB", "great britain": "GB", "britain": "GB",
  "england": "GB", "scotland": "GB", "wales": "GB", "northern ireland": "GB",
  // Renamed countries
  "swaziland": "SZ",     // → Eswatini
  "burma": "MM",         // → Myanmar
  "czech republic": "CZ", // → Czechia
  "macedonia": "MK",     // → North Macedonia
  "ivory coast": "CI",   // alias of Côte d’Ivoire
  "cote d'ivoire": "CI",
  "côte d’ivoire": "CI",
  "holland": "NL",       // → Netherlands
  "the netherlands": "NL",
  "south korea": "KR", "north korea": "KP",
  "russia": "RU", "russian federation": "RU",
  "vatican": "VA", "vatican city state": "VA",
  "east timor": "TL",    // → Timor-Leste
  "cape verde": "CV",
  "myanmar (burma)": "MM",
  "uae": "AE", "emirates": "AE",
  "drc": "CD", "dr congo": "CD", "democratic republic of the congo": "CD",
  "republic of the congo": "CG",
};

/** Resolve any user-typed name/alias to ISO alpha-2 (or null). */
export function resolveCountry(input) {
  if (!input) return null;
  const s = String(input).trim();
  if (!s) return null;
  if (_BY_CODE[s.toUpperCase()]) return s.toUpperCase();
  const lower = s.toLowerCase();
  if (COUNTRY_ALIASES[lower]) return COUNTRY_ALIASES[lower];
  const byName = COUNTRY_LIST.find((c) => c.name.toLowerCase() === lower);
  return byName ? byName.code : null;
}

export function countryName(code) {
  if (!code) return "";
  const e = _BY_CODE[String(code).toUpperCase()];
  return e ? e.name : "";
}

/** Returns a React node rendering an SVG flag.
 *  - For Kurdistan ("KU") returns the inline SVG we ship.
 *  - For Assyrian ("XAS") returns the custom SVG shipped at /flags/xas.svg.
 *  - For every other ISO alpha-2 we render a <span className="fi fi-xx" /> that
 *    `flag-icons` styles with a CSS background-image SVG. Works on every
 *    browser including Windows (which doesn't render Unicode regional-indicator
 *    emoji as flags).
 */
export function flagFromCode(code, opts = {}) {
  if (!code) return null;
  const s = String(code).trim().toUpperCase();
  if (s === "KU") return <KurdistanFlag size={opts.size === "1.25em" ? 22 : 18} />;
  if (s === "XAS") {
    const width = opts.size || "1.4em";
    return (
      <span
        role="img"
        aria-label="Assyrian flag"
        title="Assyrian"
        data-testid="flag-XAS"
        className={opts.className || ""}
        style={{
          display: "inline-block",
          width,
          height: `calc(${width} * 0.75)`,
          borderRadius: 2,
          verticalAlign: "middle",
          backgroundImage: "url('/flags/xas.svg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          boxShadow: "0 0 0 1px rgba(255,255,255,0.08)",
        }}
      />
    );
  }
  if (s.length !== 2 || !/^[A-Z]{2}$/.test(s)) return null;
  const entry = _BY_CODE[s];
  if (entry && entry.custom) return null;  // future-proof: unknown custom entry
  const lower = s.toLowerCase();
  const label = (entry && entry.name) ? `${entry.name} flag` : `${s} flag`;
  // Inline width keeps the flag at ~text height while preserving 4:3 aspect.
  const width = opts.size || "1.4em";
  return (
    <span
      role="img"
      aria-label={label}
      title={entry ? entry.name : s}
      data-testid={`flag-${s}`}
      className={`fi fi-${lower} ${opts.className || ""}`.trim()}
      style={{
        display: "inline-block",
        width,
        // 3:4 aspect (flag-icons default)
        height: `calc(${width} * 0.75)`,
        borderRadius: 2,
        verticalAlign: "middle",
        backgroundSize: "cover",
        backgroundPosition: "center",
        boxShadow: "0 0 0 1px rgba(255,255,255,0.08)",
      }}
    />
  );
}

/** Plain-emoji variant for any caller that absolutely needs a string (e.g.
 *  native <option> elements that can't host JSX). Kurdistan/Assyrian have no
 *  Unicode flag — returns "" so callers can fall back to text. */
export function flagEmojiFromCode(code) {
  if (!code) return "";
  const s = String(code).trim().toUpperCase();
  if (s.length !== 2) return "";
  const entry = _BY_CODE[s];
  if (entry && entry.custom) return "";  // native <option> can't render SVG
  const A = 0x1f1e6;
  return (
    String.fromCodePoint(A + s.charCodeAt(0) - 65) +
    String.fromCodePoint(A + s.charCodeAt(1) - 65)
  );
}
